import React, {useEffect} from "react";
import {PAGE_URLS} from "constants/AppConstants.js";
import BrowserService from "services/BrowserService.js";
import {registerPlugin} from "@capacitor/core";
import {useHistory} from "react-router-dom";
import {isAndroid} from "../../FirebaseUtils";
import LogService from "services/LogService";
import {useAppUser} from "contexts/AppUserContext";
import {syncLocalRecordingFiles} from "services/syncLocalService";
import {setupNativeUnitPreferencesListener} from "utils/nativeUnitPreferencesSync";

const TabBarPlugin: any = registerPlugin("TabBarPlugin");

const TAB_TO_URL = {
  diary: PAGE_URLS.diaryPageUrl,
  challenges: PAGE_URLS.challengesPageUrl,
  record: PAGE_URLS.aroundMePageUrl,
  explore: PAGE_URLS.aroundMePageUrl
  // account is handled natively on Android — no WebView navigation
};

function BottomBar({isNavDisabled}: {isNavDisabled?: boolean}) {
  const history = useHistory();
  const {appUser} = useAppUser();

  useEffect((): any => {
    if (!isAndroid) return;

    let listener: any;
    let unitPreferencesListener: any;
    console.log("Setting up TabBarPlugin listener");

    (async () => {
      try {
        unitPreferencesListener = await setupNativeUnitPreferencesListener();

        listener = await TabBarPlugin.addListener("tabChange", (info: any) => {
          // Run navigation on next tick so the listener returns immediately and never
          // blocks the native side or a slow WebView page.
          const runNavigation = () => {
            const targetUrl = TAB_TO_URL[info.tab];
            const currentPath = BrowserService.getPathname();

            // Same-tab tap (`info.reset === true`) — the user is asking to
            // "reset" the tab so a stuck page can be recovered. We:
            //   1. `history.replace` back to the tab root if we're on a sub-route
            //      (replace, not push, so we don't grow the history stack).
            //   2. Scroll the document back to the top — synchronous, GPU-only.
            //   3. Broadcast a `ws-tab-reset` window event so individual pages
            //      (e.g. ChallengesPage) can close any open modals / drawers /
            //      sub-views in their own state.
            if (info.reset) {
              try {
                if (targetUrl && currentPath !== targetUrl) {
                  history.replace(targetUrl);
                }
              } catch (_) {
                // navigation failure must never break the tap-feedback loop
              }
              try {
                window.scrollTo({top: 0, left: 0, behavior: "auto"});
              } catch (_) {
                // older WebViews don't accept the options object
                try {
                  window.scrollTo(0, 0);
                } catch (__) {
                  // both forms unsupported — give up silently
                }
              }
              try {
                window.dispatchEvent(new CustomEvent("ws-tab-reset", {detail: {tab: info.tab}}));
              } catch (_) {
                // CustomEvent unsupported (very old WebView) — silent fallback
              }
              return;
            }

            if (targetUrl && currentPath !== targetUrl) {
              history.push(targetUrl);
            }

            if (info.activityId) {
              syncLocalRecordingFiles(null, appUser.uid, 1).then(() => {
                const diaryUrl = TAB_TO_URL[info.tab];
                if (diaryUrl) {
                  history.push({
                    pathname: diaryUrl,
                    state: {newlyCreatedActivityId: info.activityId}
                  });
                }
              });
            }
          };

          if (typeof requestAnimationFrame !== "undefined") {
            requestAnimationFrame(runNavigation);
          } else {
            setTimeout(runNavigation, 0);
          }
        });
      } catch (error) {
        LogService.addErrorLogAsync(null, "TabBarPlugin: Error setting up listener", error);
      }
    })();

    return async () => {
      if (listener) {
        await listener.remove();
      }
      if (unitPreferencesListener) {
        await unitPreferencesListener.remove();
      }
    };
  }, [history, appUser]);

  if (isNavDisabled) {
    return null;
  }

  return <></>;
}

export default BottomBar;
